import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthProvider';
import ProtectedRoute from './auth/ProtectedRoute';
import Login from './auth/Login';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { LoadingCard } from './components/QueryStates';
import { queryClient } from './lib/queryClient';

// Per-route code-splitting. Each page becomes its own JS chunk that
// only downloads when the user navigates to it. First-paint payload
// drops from ~1.2 MB to <250 KB gzipped because the user only gets
// the route they actually opened.
const PipelineHealth = lazy(() => import('./pages/PipelineHealth'));
const PdfTracker = lazy(() => import('./pages/PdfTracker'));
const ModelCoverage = lazy(() => import('./pages/ModelCoverage'));
const CoverageMatrix = lazy(() => import('./pages/CoverageMatrix'));
const CostVolume = lazy(() => import('./pages/CostVolume'));
const CostBatches = lazy(() => import('./pages/CostBatches'));
const UnmappedCenter = lazy(() => import('./pages/UnmappedCenter'));
const ExportCenter = lazy(() => import('./pages/ExportCenter'));
const StuckQueue = lazy(() => import('./pages/StuckQueue'));
const IngestionStatus = lazy(() => import('./pages/IngestionStatus'));
const DownstreamStatus = lazy(() => import('./pages/DownstreamStatus'));
const AutoOnboarding = lazy(() => import('./pages/AutoOnboarding'));
const LoadStatus = lazy(() => import('./pages/LoadStatus'));
const LoadStatusDetail = lazy(() => import('./pages/LoadStatusDetail'));
const JcbDeliveryGap = lazy(() => import('./pages/JcbDeliveryGap'));
const LoadableNow = lazy(() => import('./pages/LoadableNow'));

function RouteFallback() {
  return <LoadingCard label="Loading…" />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  {/* Default landing → Auto-Onboarding (the page
                      fully wired against the live API). */}
                  <Route index element={<Navigate to="/auto-onboarding" replace />} />
                  <Route path="pipeline-health" element={<PipelineHealth />} />
                  <Route path="pdfs" element={<PdfTracker />} />
                  <Route path="models" element={<ModelCoverage />} />
                  <Route path="coverage" element={<CoverageMatrix />} />
                  <Route path="costs" element={<CostVolume />} />
                  <Route path="costs/batches" element={<CostBatches />} />
                  <Route path="unmapped" element={<UnmappedCenter />} />
                  <Route path="stuck" element={<StuckQueue />} />
                  <Route path="ingestion" element={<IngestionStatus />} />
                  <Route path="export" element={<ExportCenter />} />
                  <Route path="downstream/:model" element={<DownstreamStatus />} />
                  <Route path="auto-onboarding" element={<AutoOnboarding />} />
                  <Route path="load-status" element={<LoadStatus />} />
                  <Route
                    path="load-status/delivery-gap"
                    element={<JcbDeliveryGap />}
                  />
                  <Route
                    path="load-status/loadable"
                    element={<LoadableNow />}
                  />
                  {/* Catch-all `*` keeps `/` in master line names intact
                      (e.g. `320/50600`) — the page reads useParams()['*']. */}
                  <Route
                    path="load-status/lines/*"
                    element={<LoadStatusDetail />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
